import * as React from 'react';

import BaseIndex from '../../common/sound_usage_histories/Index';

import NavigationProp from '../../../../../navigators/NavigationProp';
import {SceneFormBackgroundMusicShowSceneCommandEditSoundUsageHistoriesRouteProp} from '../../../../../navigators/RouteProps';

import * as routers from '../../../../../routers';

import {Params as SoundUsageHistoryIndexParams} from '../../../../../actions/sound_usage_histories/index';
import {Params as SoundUsageHistoryUpdateParams} from '../../../../../actions/sound_usage_histories/update';
import {Params as SceneCommandFormCreateParams} from '../../../../../actions/scene_command_forms/create';
import {Params as SceneCommandFormUpdateParams} from '../../../../../actions/scene_command_forms/update';

import CompositeSequenceSceneCommandFormFactory from '../../../../../view_models/CompositeSequenceSceneCommandFormFactory';

import Sound from '../../../../../../domain/entities/Sound';
import SoundUsageHistory from '../../../../../../domain/entities/SoundUsageHistory';
import CurrentUser from '../../../../../../domain/entities/writer/CurrentUser';
import SceneCommandForm from '../../../../../../domain/forms/scene_commands/SceneCommandForm';
import BackgroundMusicShowSceneCommandForm from '../../../../../../domain/forms/scene_commands/BackgroundMusicShowSceneCommandForm';
import IllustrationShowSceneCommandForm from '../../../../../../domain/forms/scene_commands/IllustrationShowSceneCommandForm';
import CompositeSequenceSceneCommandForm from '../../../../../../domain/forms/scene_commands/CompositeSequenceSceneCommandForm';
import PaginatedResult from '../../../../../../domain/results/PaginatedResult';
import SceneForm from '../../../../../../domain/forms/SceneForm';

export interface Params {
  storyId: number;
  episodeId?: number;
  sceneId?: number;
  back?: boolean;
  sceneCommandId: number;
  parentSceneCommandId?: number;
}

export interface StateProps {
  navigation: NavigationProp;
  route: SceneFormBackgroundMusicShowSceneCommandEditSoundUsageHistoriesRouteProp;
  sceneForm: SceneForm | null;
  soundUsageHistoriesParams: SoundUsageHistoryIndexParams;
  soundUsageHistories: SoundUsageHistory[] | null;
  sceneCommandForm: SceneCommandForm | null;
  parentSceneCommandForm: SceneCommandForm | null;
  currentUser: CurrentUser | null;
  enabledFavorite?: boolean;
}

export interface DispatchProps {
  indexSoundUsageHistories: (
    params: SoundUsageHistoryIndexParams,
  ) => Promise<PaginatedResult<SoundUsageHistory>>;
  createSceneCommandForm: (
    params: SceneCommandFormCreateParams,
  ) => Promise<any>;
  updateSceneCommandForm: (
    params: SceneCommandFormUpdateParams,
  ) => Promise<any>;
  updateSoundUsageHistory: (
    id: number,
    params: SoundUsageHistoryUpdateParams,
  ) => Promise<SoundUsageHistory>;
}

interface Props extends StateProps, DispatchProps {}

export default class Index extends React.Component<Props> {
  private disabledSubmit = false;

  public render(): React.ReactNode {
    return (
      <BaseIndex
        {...this.props}
        onForwardToNewSoundUsageHistory={
          this.handleForwardToNewSoundUsageHistory
        }
        onSelectSoundUsageHistory={this.handleSelectSoundUsageHistory}
        onForwardToFavoriteList={this.handleForwardToFavoriteList}
        onSelectSound={this.handleSelectSound}
      />
    );
  }

  private handleSelectSoundUsageHistory = (
    soundUsageHistory: SoundUsageHistory,
  ) => {
    const {
      route,
      navigation,

      parentSceneCommandForm,
      updateSceneCommandForm,
      updateSoundUsageHistory,
    } = this.props;
    if (this.disabledSubmit) {
      return;
    }
    this.disabledSubmit = true;
    const {storyId, sceneCommandId} = route.params;
    const {sound} = soundUsageHistory;
    const illustrationShowSceneCommandForm =
      this.fetchIllustrationShowSceneCommandFormFromParent();
    if (illustrationShowSceneCommandForm) {
      const sceneCommandForm = new IllustrationShowSceneCommandForm(
        illustrationShowSceneCommandForm.illustration,
        sound,
        null,
        null,
        sceneCommandId,
      );
      if (parentSceneCommandForm) {
        updateSceneCommandForm({
          sceneCommandForm: CompositeSequenceSceneCommandFormFactory.update(
            parentSceneCommandForm,
            sceneCommandForm,
          ),
        });
      } else {
        updateSceneCommandForm({sceneCommandForm});
      }
    } else {
      const sceneCommandForm = new BackgroundMusicShowSceneCommandForm(
        sound,
        sceneCommandId,
      );
      if (parentSceneCommandForm) {
        updateSceneCommandForm({
          sceneCommandForm: CompositeSequenceSceneCommandFormFactory.update(
            parentSceneCommandForm,
            sceneCommandForm,
          ),
        });
      } else {
        updateSceneCommandForm({sceneCommandForm});
      }
    }
    updateSoundUsageHistory(sound.id, {
      storyId,
      soundType: 'background_music',
    }).then(() => {
      (navigation.getParent() || navigation).goBack();
    });
  };

  private handleForwardToNewSoundUsageHistory = () => {
    const {navigation, route} = this.props;
    const {storyId, episodeId, sceneId, sceneCommandId, parentSceneCommandId} =
      route.params;
    routers.linkToSceneFormBackgroundMusicShowSceneCommandEditSoundSearchForm(
      navigation,
      {
        storyId,
        episodeId,
        sceneId,
        sceneCommandId,
        parentSceneCommandId,
      },
    );
  };

  private fetchIllustrationShowSceneCommandFormFromParent =
    (): IllustrationShowSceneCommandForm | null => {
      const {sceneCommandForm, parentSceneCommandForm} = this.props;
      if (sceneCommandForm instanceof IllustrationShowSceneCommandForm) {
        return sceneCommandForm;
      }
      if (parentSceneCommandForm instanceof CompositeSequenceSceneCommandForm) {
        const found = parentSceneCommandForm.commandForms.find(
          c => c instanceof IllustrationShowSceneCommandForm,
        );
        if (found instanceof IllustrationShowSceneCommandForm) {
          return found;
        }
      }
      return null;
    };

  private handleForwardToFavoriteList = () => {
    const {navigation, route} = this.props;
    const {storyId, episodeId, sceneId, sceneCommandId, parentSceneCommandId} =
      route.params;
    routers.linkToSceneFormBackgroundMusicShowSceneCommandEditSounds(
      navigation,
      {
        storyId,
        episodeId,
        sceneId,
        sceneCommandId,
        parentSceneCommandId,
        favorite: true,
      },
    );
  };

  private handleSelectSound = (sound: Sound): void => {
    if (this.disabledSubmit) {
      return;
    }
    this.disabledSubmit = true;
    const {
      route,
      navigation,
      parentSceneCommandForm,
      updateSceneCommandForm,
      updateSoundUsageHistory,
    } = this.props;
    const {storyId, sceneCommandId} = route.params;
    const illustrationShowSceneCommandForm =
      this.fetchIllustrationShowSceneCommandFormFromParent();
    if (illustrationShowSceneCommandForm) {
      const sceneCommandForm = new IllustrationShowSceneCommandForm(
        illustrationShowSceneCommandForm.illustration,
        sound,
        null,
        null,
        sceneCommandId,
      );
      if (parentSceneCommandForm) {
        updateSceneCommandForm({
          sceneCommandForm: CompositeSequenceSceneCommandFormFactory.update(
            parentSceneCommandForm,
            sceneCommandForm,
          ),
        });
      } else {
        updateSceneCommandForm({sceneCommandForm});
      }
    } else {
      const sceneCommandForm = new BackgroundMusicShowSceneCommandForm(
        sound,
        sceneCommandId,
      );
      if (parentSceneCommandForm) {
        updateSceneCommandForm({
          sceneCommandForm: CompositeSequenceSceneCommandFormFactory.update(
            parentSceneCommandForm,
            sceneCommandForm,
          ),
        });
      } else {
        updateSceneCommandForm({
          sceneCommandForm,
        });
      }
    }
    updateSoundUsageHistory(sound.id, {
      storyId,
      soundType: 'background_music',
    }).then(() => {
      (navigation.getParent() || navigation).goBack();
    });
  };
}
